import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import StaffNotation from '@/components/feature/StaffNotation';
import Button from '@/components/base/Button';
import Badge from '@/components/base/Badge';
import { getNote, FIRST_REGION_IDS } from '@/lib/music/notes';

const NAMES = ['Dó', 'Ré', 'Mi', 'Fá', 'Sol', 'Lá', 'Si'];

interface QuizItem {
  noteId: string;
  answer: string;
}

function shuffle<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function buildQuiz(): QuizItem[] {
  const ids = shuffle(FIRST_REGION_IDS).slice(0, 7);
  return ids.map((noteId) => ({ noteId, answer: getNote(noteId).ptName }));
}

export default function PlacementTestPage() {
  const [quiz, setQuiz] = useState<QuizItem[]>(() => buildQuiz());
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [correct, setCorrect] = useState(0);
  const [finished, setFinished] = useState(false);

  const item = quiz[index];
  const total = quiz.length;

  const recommendation = useMemo(() => {
    if (correct >= 6) {
      return {
        title: 'Você já identifica bem as notas',
        text: 'Você pode ir direto para a revisão das sete notas ou começar pelo treino de ritmo.',
        primaryTo: '/aulas/10',
        primaryLabel: 'Ir para a revisão (aula 10)',
        secondaryTo: '/ritmo',
        secondaryLabel: 'Treinar ritmo',
      };
    }
    if (correct >= 3) {
      return {
        title: 'Bom começo!',
        text: 'Você já reconhece algumas notas. Uma passada rápida pelas primeiras aulas vai firmar o restante.',
        primaryTo: '/aulas/4',
        primaryLabel: 'Começar na aula 4',
        secondaryTo: '/aulas/1',
        secondaryLabel: 'Começar do início',
      };
    }
    return {
      title: 'Vamos começar do começo',
      text: 'Sem problema — o curso foi feito exatamente para isso. A primeira aula é descobrir o Dó central.',
      primaryTo: '/aulas/1',
      primaryLabel: 'Começar na aula 1',
      secondaryTo: '/aulas',
      secondaryLabel: 'Ver todas as aulas',
    };
  }, [correct]);

  const choose = (name: string) => {
    if (selected !== null) return;
    setSelected(name);
    if (name === item.answer) setCorrect((value) => value + 1);
  };

  const next = () => {
    if (index + 1 >= total) {
      setFinished(true);
      return;
    }
    setIndex((value) => value + 1);
    setSelected(null);
  };

  const restart = () => {
    setQuiz(buildQuiz());
    setIndex(0);
    setSelected(null);
    setCorrect(0);
    setFinished(false);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="w-full bg-background-100 px-4 md:px-8 py-12 md:py-16">
          <div className="mx-auto max-w-4xl">
            <Badge tone="primary" icon="ri-compass-3-line">
              Teste de entrada (opcional)
            </Badge>
            <h1 className="mt-4 font-heading text-3xl font-extrabold text-foreground-950 md:text-4xl">
              Você já conhece os nomes das notas?
            </h1>
            <p className="mt-4 text-foreground-700">
              Este teste ajuda a sugerir por onde começar. Ele é totalmente opcional e não bloqueia
              nada: você pode ir direto para qualquer aula quando quiser.
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="mx-auto max-w-4xl">
            {finished ? (
              <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center md:p-10">
                <span className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-compass-3-line text-3xl" />
                </span>
                <h2 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
                  {recommendation.title}
                </h2>
                <p className="mt-2 text-sm text-foreground-700">
                  Você acertou {correct} de {total} notas.
                </p>
                <p className="mx-auto mt-3 max-w-lg text-foreground-700">{recommendation.text}</p>
                <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
                  <Link to={recommendation.primaryTo} className="contents">
                    <Button variant="primary" size="lg" icon="ri-play-circle-line">
                      {recommendation.primaryLabel}
                    </Button>
                  </Link>
                  <Link to={recommendation.secondaryTo} className="contents">
                    <Button variant="outline" size="lg" icon="ri-arrow-right-line">
                      {recommendation.secondaryLabel}
                    </Button>
                  </Link>
                </div>
                <button
                  type="button"
                  onClick={restart}
                  className="mt-6 text-sm font-label font-semibold text-foreground-600 hover:text-primary-700 cursor-pointer"
                >
                  Refazer o teste
                </button>
              </div>
            ) : (
              <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
                <div className="flex items-center justify-between">
                  <Badge tone="primary" icon="ri-question-line">
                    Nota {index + 1} de {total}
                  </Badge>
                  <span className="text-xs font-label font-semibold text-foreground-500">
                    Acertos: {correct}
                  </span>
                </div>

                <div className="mt-4 rounded-lg bg-background-100 p-3 md:p-5">
                  <StaffNotation
                    notes={[item.noteId]}
                    feedbackIndex={selected !== null ? 0 : undefined}
                    feedbackType={selected !== null ? (selected === item.answer ? 'correct' : 'wrong') : null}
                    space={26}
                  />
                </div>

                <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">
                  Qual é o nome desta nota?
                </h2>

                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {NAMES.map((name) => {
                    const isAnswer = name === item.answer;
                    const isChosen = selected === name;
                    let classes =
                      'border-background-300 bg-background-50 hover:bg-background-100 text-foreground-900';
                    if (selected !== null && isAnswer) {
                      classes = 'border-accent-400 bg-accent-100 text-accent-900';
                    } else if (selected !== null && isChosen) {
                      classes = 'border-secondary-400 bg-secondary-100 text-secondary-900';
                    } else if (selected !== null) {
                      classes = 'border-background-200 bg-background-50 text-foreground-400';
                    }
                    return (
                      <button
                        key={name}
                        type="button"
                        disabled={selected !== null}
                        onClick={() => choose(name)}
                        className={`rounded-md border px-4 py-3 text-center text-sm font-label font-semibold transition-colors ${classes} ${
                          selected === null ? 'cursor-pointer' : 'cursor-default'
                        }`}
                      >
                        {name}
                      </button>
                    );
                  })}
                </div>

                {selected !== null ? (
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-foreground-700">
                      {selected === item.answer
                        ? 'Isso mesmo!'
                        : `A resposta certa era ${item.answer}.`}
                    </p>
                    <Button variant="primary" icon="ri-arrow-right-line" onClick={next}>
                      {index + 1 >= total ? 'Ver resultado' : 'Próxima nota'}
                    </Button>
                  </div>
                ) : null}
              </div>
            )}

            <p className="mt-6 text-center text-sm text-foreground-500">
              Prefere começar direto?{' '}
              <Link to="/aulas/1" className="font-label font-semibold text-primary-700 cursor-pointer">
                Ir para a aula 1
              </Link>
            </p>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}